import styled from '@emotion/styled/macro'
import { Box } from '@mui/material'
import { keyframes } from '@emotion/react'
import theme from 'theme'

const shimmer = keyframes`
  0% { transform: translateX(-100%); }
  100% { transform: translateX(800%); }
`

export const LabelSkeletonBar = styled(Box)`
  height: 108px;
  width: 100%;
  background: ${theme.colors.labelCardBg};
  border: 1px solid ${theme.colors.borderMedium};
  border-radius: ${theme.attributes.borderRadius.secondary};
  overflow: hidden;
  position: relative;
  flex-shrink: 0;

  &::after {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    width: 27px;
    height: 100%;
    border-radius: ${theme.attributes.borderRadius.tertiary};
    background: linear-gradient(
      270deg,
      rgba(48, 40, 47, 0.5) 0%,
      rgba(87, 82, 86, 0.5) 50%,
      rgba(48, 40, 47, 0.5) 100%
    );
    animation: ${shimmer} 1.5s ease-in-out infinite;
  }

  /* Matches the dashboard loading state: light placeholder with a white sweep. */
  html[data-theme='light'] & {
    background: ${theme.colors.legacyPrimaryLight3};

    &::after {
      background: linear-gradient(
        270deg,
        rgba(252, 247, 251, 0) 0%,
        rgba(252, 247, 251, 0.9) 50%,
        rgba(252, 247, 251, 0) 100%
      );
    }
  }
`

export const CountSkeleton = styled(Box)`
  height: 20px;
  width: 24px;
  background: ${theme.colors.labelCardBg};
  border: 1px solid ${theme.colors.borderMedium};
  border-radius: ${theme.attributes.borderRadius.secondary};
  overflow: hidden;
  position: relative;
  display: inline-block;

  &::after {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    width: 27px;
    height: 100%;
    border-radius: ${theme.attributes.borderRadius.tertiary};
    background: linear-gradient(
      270deg,
      rgba(48, 40, 47, 0.5) 0%,
      rgba(87, 82, 86, 0.5) 50%,
      rgba(48, 40, 47, 0.5) 100%
    );
    animation: ${shimmer} 1.5s ease-in-out infinite;
  }

  /* Matches the dashboard loading state: light placeholder with a white sweep. */
  html[data-theme='light'] & {
    background: ${theme.colors.legacyPrimaryLight3};

    &::after {
      background: linear-gradient(
        270deg,
        rgba(252, 247, 251, 0) 0%,
        rgba(252, 247, 251, 0.9) 50%,
        rgba(252, 247, 251, 0) 100%
      );
    }
  }
`

export const LabelsWrapper = styled(Box)`
  background: ${theme.colors.surfaceContainerBackground};
  border-radius: ${theme.attributes.borderRadius.tertiary};
  padding: ${theme.spacing.medium}px;
  display: flex;
  flex-direction: column;
  gap: ${theme.spacing.small}px;
  overflow: hidden;
  flex: 1;
  min-height: 0;
  box-sizing: border-box;

  .section-title {
    display: flex;
    align-items: center;
    gap: ${theme.spacing.xSmall}px;

    .title-icon {
      width: 18px;
      height: 18px;
      color: ${theme.colors.menuItemText};
    }

    .title-text {
      font-family: ${theme.typography.fontFamily.primary};
      font-weight: ${theme.typography.fontWeight.bold};
      font-size: ${theme.typography.fontSize.header};
      color: ${theme.colors.menuItemText};
    }

    .title-count {
      font-family: ${theme.typography.fontFamily.primary};
      font-weight: ${theme.typography.fontWeight.bold};
      font-size: ${theme.typography.fontSize.header};
      color: ${theme.colors.menuItemText};
    }
  }

  .empty-state {
    flex: 1;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 6px;
    text-align: center;

    svg {
      margin-bottom: 10px;
    }

    .empty-title {
      font-family: ${theme.typography.fontFamily.primary};
      font-weight: ${theme.typography.fontWeight.bold};
      font-size: ${theme.typography.fontSize.xLarge};
      color: ${theme.colors.menuItemText};
      line-height: normal;
    }

    .empty-description {
      font-family: ${theme.typography.fontFamily.primary};
      font-weight: ${theme.typography.fontWeight.normal};
      font-size: ${theme.typography.fontSize.large};
      color: ${theme.colors.devicesSecondaryText};
      line-height: 20px;
    }
  }

  .labels-list {
    display: flex;
    flex-direction: column;
    gap: ${theme.spacing.small}px;
    overflow-y: auto;
    overflow-x: hidden;
    flex: 1;
    min-width: 0;

    margin-right: -12px;
    padding-right: 4px;

    &::-webkit-scrollbar {
      background: transparent;
      width: 20px !important;
    }

    &::-webkit-scrollbar-track {
      background: transparent;
    }

    &::-webkit-scrollbar-thumb {
      background-color: ${theme.colors.borderMedium};
      border-radius: 9999px;
      border-right: 8px solid transparent;
      border-left: 8px solid transparent;
      background-clip: padding-box;
    }
  }
`

export const LabelCardWrapper = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'labelColor' && prop !== 'labelLightColor'
})<{ labelColor: string; labelLightColor: string }>`
  background: ${theme.colors.labelCardBg};
  border: 1px solid ${theme.colors.borderMedium};
  border-radius: ${theme.attributes.borderRadius.secondary};
  padding: ${theme.spacing.small}px;
  display: flex;
  flex-direction: column;
  gap: 12px;
  min-width: 0;
  transition: background 0.2s ease, border-color 0.2s ease;

  &:hover {
    background: ${theme.colors.cardFillTertiary};
    border-color: ${theme.colors.strokePrimary};
  }

  html[data-theme='light'] & {
    background: #fafafb;
    border-color: #f0f0f1;

    &:hover {
      background: ${theme.colors.cardFillTertiary};
      border-color: ${theme.colors.strokePrimary};
    }
  }

  .label-header {
    display: flex;
    align-items: center;
    gap: ${theme.spacing.xSmall}px;
    width: 100%;

    .label-icon {
      width: 20px;
      height: 20px;
      min-width: 20px;
      color: ${theme.colors.dropdownLabel};
    }

    .label-title {
      font-family: ${theme.typography.fontFamily.primary};
      font-weight: ${theme.typography.fontWeight.extraThick};
      font-size: ${theme.typography.fontSize.large};
      color: ${(props) => props.labelColor};
      line-height: 20px;
      flex: 1;

      html[data-theme='light'] & {
        color: ${(props) => props.labelLightColor};
      }
    }

    .cpi-chip {
      background: ${theme.colors.btnTertiaryBg};
      border-radius: 18px;
      padding: 2px ${theme.spacing.xSmall}px;
      height: 24px;
      width: 72px;
      display: flex;
      align-items: center;
      justify-content: center;
      cursor: pointer;

      span {
        font-family: ${theme.typography.fontFamily.primary};
        font-weight: ${theme.typography.fontWeight.bold};
        font-size: ${theme.typography.fontSize.medium};
        color: ${theme.colors.devicesSecondaryText};
        letter-spacing: 0.5px;
        line-height: 16px;
      }

      html[data-theme='light'] & {
        background: #e4e4e9;

        span {
          color: #4a4a54;
        }
      }
    }
  }

  .label-description {
    font-family: ${theme.typography.fontFamily.primary};
    font-weight: ${theme.typography.fontWeight.bold};
    font-size: ${theme.typography.fontSize.medium};
    color: ${theme.colors.devicesSecondaryText};
    line-height: 16px;
    overflow-wrap: anywhere;
    min-width: 0;

    html[data-theme='light'] & {
      color: ${theme.colors.foreground};
    }
  }

  .label-sla-change {
    display: flex;
    align-items: center;
    gap: 4px;
    font-family: ${theme.typography.fontFamily.primary};
    font-weight: ${theme.typography.fontWeight.bold};
    font-size: ${theme.typography.fontSize.medium};
    color: ${theme.colors.devicesSecondaryText};
    line-height: 16px;

    .arrow-icon {
      width: 12px;
      height: 12px;
      color: ${theme.colors.devicesSecondaryText};
    }
  }

  .label-author {
    display: flex;
    gap: 6px;
    font-family: ${theme.typography.fontFamily.primary};
    font-weight: ${theme.typography.fontWeight.bold};
    font-size: ${theme.typography.fontSize.medium};
    line-height: 16px;

    .author-prefix {
      color: ${theme.colors.dropdownLabel};
    }

    .author-name {
      color: ${theme.colors.devicesSecondaryText};
    }
  }

  .label-footer {
    display: flex;
    align-items: center;
    justify-content: space-between;
    width: 100%;

    .label-date {
      font-family: ${theme.typography.fontFamily.primary};
      font-weight: ${theme.typography.fontWeight.bold};
      font-size: ${theme.typography.fontSize.medium};
      color: ${theme.colors.dropdownLabel};
      line-height: 16px;

      html[data-theme='light'] & {
        color: ${theme.colors.textCaption};
      }
    }

    .visibility-icon {
      width: 16px;
      height: 16px;
      color: ${theme.colors.dropdownLabel};
    }
  }
`
